/**
 * DOM UI overlays for Weird Kart Racer: main menu, sign-in, lobby, results and
 * the global leaderboard screen.
 *
 * The UI lives inside #ui-root, which has pointer-events:none so gameplay input
 * falls through to the canvas. Interactive panels re-enable pointer-events on
 * themselves. All state is driven from the outside via show/hide methods; user
 * actions are surfaced through the injected UICallbacks. Re-rendered each call.
 *
 * Every length below is in `rem` — a multiple of the viewport-derived root font
 * size set in src/style.css. One panel definition therefore covers a 1366-wide
 * laptop and a 4K monitor, and a window dragged from one screen to the other
 * re-lays out on the spot, since nothing here caches a pixel size.
 */
import {
  ACCOUNT_NAME_MAX,
  BOT_SKILLS,
  LobbyInfo,
  LeaderboardEntry,
  MAX_BOTS,
  MAX_PLAYERS,
  PASSWORD_MAX,
  accountNameError,
  passwordError,
  type BotSkill,
} from '../shared/protocol';
import { VehicleStats } from './types';
import { TrackInfo } from './track';
import { buildJoinUrl } from './joinlink';
import { QUALITY_LABELS, QUALITY_LEVELS, type QualityLevel } from './quality';

export interface UICallbacks {
  onCreateLobby(name: string, vehicleId: string): void;
  onJoinLobby(code: string, name: string, vehicleId: string): void;
  onSelectVehicle(vehicleId: string): void;
  onToggleReady(ready: boolean): void;
  onStartRace(): void;
  onLeaveLobby(): void;
  onRematch(): void;
  /** Host cycled the lobby's track selection. */
  onSelectTrack(trackId: string): void;
  /** Open the leaderboard, filtered to `trackId` (null = all tracks). */
  onShowLeaderboard(trackId: string | null): void;
  /** Close the leaderboard and return to the previous screen. */
  onCloseLeaderboard(): void;
  /** Host changed how many bots fill out the grid. */
  onSetBots(count: number): void;
  /** Host changed how hard those bots try. */
  onSetBotSkill(skill: BotSkill): void;
  /** Player picked a graphics preset. */
  onSelectQuality(level: QualityLevel): void;
  /** Open the sign-in / create-account screen. */
  onShowAuth(): void;
  /** Leave the sign-in screen without signing in. */
  onCloseAuth(): void;
  onLogin(name: string, password: string): void;
  onRegister(name: string, password: string): void;
  onLogout(): void;
}

const STYLE_ID = 'wkr-ui-style';

/** The screens this overlay can be showing. */
type ScreenId = 'menu' | 'lobby' | 'results' | 'leaderboard' | 'auth';

/** Which half of the sign-in screen is on show. */
type AuthMode = 'login' | 'register';

/** Scroll offsets carried across a re-render of the same screen. */
interface ScrollState {
  overlay: number;
  players: number;
}

/** Focused text input and caret position, carried across a re-render. */
interface FocusState {
  field: string;
  start: number;
  end: number;
}

/** Goofy random name fragments for the prefilled name input. */
const NAME_ADJ = [
  'Turbo',
  'Wobbly',
  'Zoomy',
  'Spicy',
  'Sneaky',
  'Chunky',
  'Wiggly',
  'Rowdy',
  'Cosmic',
  'Greasy',
  'Bouncy',
  'Feral',
];
const NAME_NOUN = [
  'Banana',
  'Goose',
  'Waffle',
  'Gremlin',
  'Muffin',
  'Pickle',
  'Noodle',
  'Raccoon',
  'Toaster',
  'Wombat',
  'Cactus',
  'Blobfish',
];

function randomName(): string {
  const a = NAME_ADJ[Math.floor(Math.random() * NAME_ADJ.length)];
  const n = NAME_NOUN[Math.floor(Math.random() * NAME_NOUN.length)];
  const num = Math.floor(Math.random() * 90) + 10;
  return `${a}${n}${num}`;
}

/** Format ms as m:ss.mmm, or "DNF" when the player didn't finish. */
function formatTime(ms: number | null): string {
  if (ms === null || !Number.isFinite(ms)) return 'DNF';
  const totalMs = Math.max(0, Math.round(ms));
  const minutes = Math.floor(totalMs / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);
  const millis = totalMs % 1000;
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${millis
    .toString()
    .padStart(3, '0')}`;
}

/** Ordinal suffix for placing text ("1st", "2nd", "3rd", "4th"...). */
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

/** Normalise a 0..1-ish stat to a percentage width for a bar. */
function statPct(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.max(4, Math.min(100, (value / max) * 100));
}

/** Filled/empty star string for a 1..3 difficulty. */
function difficultyStars(difficulty: number): string {
  const filled = Math.max(0, Math.min(3, Math.round(difficulty)));
  return '★'.repeat(filled) + '☆'.repeat(3 - filled);
}

/** Human-readable name for each bot skill setting. */
const BOT_SKILL_LABELS: Record<BotSkill, string> = {
  rookie: '🐢 Rookie',
  club: '🙂 Club',
  pro: '😤 Pro',
  ace: '🔥 Ace',
};

/** Medal emoji for the top three ranks, else empty string. */
function medal(rank: number): string {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return '';
}

const CSS = `
.wkr-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  /* Centring is done with auto margins on the panel rather than align-items:
     a flex item centred by alignment gets its overflow clipped off the TOP of
     the scroll box, which on a short window makes the title unreachable. */
  align-items: flex-start;
  justify-content: center;
  padding: 1.5rem;
  overflow-y: auto;
  pointer-events: auto;
  font-family: inherit;
  color: var(--text);
}
.wkr-panel {
  pointer-events: auto;
  width: min(41.25rem, 96vw);
  margin: auto;
  /* Lit from above: a faint sheen across the top edge sells the glass. */
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0) 38%),
    var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 1.38rem;
  padding: 1.88rem 2rem 2rem;
  box-shadow:
    0 1.88rem 5rem rgba(0, 0, 0, 0.6),
    0 2px 0.5rem rgba(0, 0, 0, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(18px) saturate(1.25);
  animation: wkr-rise 0.34s cubic-bezier(0.2, 0.9, 0.3, 1) both;
}
@keyframes wkr-rise {
  from { opacity: 0; transform: translateY(0.88rem) scale(0.985); }
  to { opacity: 1; transform: none; }
}
/* A repaint of the screen already on show, rather than an arrival at it. */
.wkr-panel.wkr-instant { animation: none; }
.wkr-title {
  text-align: center;
  font-size: 3.4rem;
  font-weight: 900;
  letter-spacing: -0.01em;
  margin-bottom: 0.38rem;
  background: linear-gradient(100deg, var(--accent), var(--accent-2) 45%, var(--accent-3) 80%, var(--accent));
  background-size: 260% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 3px 22px rgba(255, 93, 162, 0.28));
  animation: wkr-sheen 9s linear infinite;
}
@keyframes wkr-sheen {
  from { background-position: 0% 50%; }
  to { background-position: 260% 50%; }
}
/* Emoji must opt out of the gradient: background-clip:text would fill the
   colour glyph with the gradient (or a solid block where it falls back). */
.wkr-title-emoji {
  -webkit-background-clip: initial;
  background-clip: initial;
  -webkit-text-fill-color: initial;
  color: initial;
  filter: drop-shadow(0 0.19rem 0.63rem rgba(0, 0, 0, 0.5));
  margin-right: 0.22em;
}
.wkr-subtitle {
  text-align: center;
  color: var(--muted);
  margin-bottom: 1.25rem;
  font-size: 0.94rem;
}
.wkr-label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--muted);
  margin: 0.75rem 0 0.3rem;
  font-weight: 700;
}
.wkr-input {
  width: 100%;
  padding: 0.75rem 0.88rem;
  font-size: 1rem;
  color: var(--text);
  background: rgba(0, 0, 0, 0.32);
  border: 1px solid var(--panel-border);
  border-radius: 0.75rem;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.wkr-input:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(255, 207, 51, 0.18);
}
.wkr-code-input {
  text-transform: uppercase;
  letter-spacing: 0.25rem;
  font-weight: 800;
  text-align: center;
}
.wkr-btn {
  pointer-events: auto;
  cursor: pointer;
  font-family: inherit;
  font-weight: 800;
  font-size: 0.94rem;
  letter-spacing: 0.01em;
  color: #100f18;
  background: linear-gradient(100deg, var(--accent), var(--accent-2));
  border: none;
  border-radius: 0.75rem;
  padding: 0.81rem 1.13rem;
  transition: transform 0.09s ease, filter 0.15s ease, box-shadow 0.18s ease, opacity 0.15s;
  box-shadow: 0 0.63rem 1.63rem rgba(255, 93, 162, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}
.wkr-btn:hover { filter: brightness(1.1); transform: translateY(-1px); box-shadow: 0 0.88rem 2rem rgba(255, 93, 162, 0.38); }
.wkr-btn:active { transform: translateY(1px) scale(0.99); box-shadow: 0 0.38rem 1rem rgba(255, 93, 162, 0.28); }
.wkr-btn:disabled { opacity: 0.45; cursor: default; box-shadow: none; transform: none; filter: none; }
.wkr-btn-block { width: 100%; }
.wkr-btn-big { font-size: 1.13rem; padding: 1rem; }
.wkr-btn-ghost {
  color: var(--text);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--panel-border);
  box-shadow: none;
}
.wkr-btn-ghost:hover { background: rgba(255, 255, 255, 0.12); filter: none; }
.wkr-join-row { display: flex; gap: 0.63rem; align-items: stretch; }
.wkr-join-row .wkr-input { flex: 1; }
.wkr-btn-sm { font-size: 0.81rem; padding: 0.5rem 0.75rem; border-radius: 0.63rem; }

/* Account strip. Sits above the title on the menu: who you are racing as is the
   first thing that decides whether a run counts, so it reads before anything
   else — but it is a status line, not the headline, hence the small type. */
.wkr-account {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.63rem;
  flex-wrap: wrap;
  margin-bottom: 0.75rem;
}
.wkr-account-who { font-size: 0.81rem; color: var(--muted); min-width: 0; }
.wkr-account-name { color: var(--accent); font-weight: 800; }
.wkr-account-note { font-size: 0.75rem; }
/* The name a signed-in player races under: not editable, so not an input. */
.wkr-idname {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 0.88rem;
  border: 1px solid var(--panel-border);
  border-radius: 0.75rem;
  background: rgba(255, 207, 51, 0.07);
  font-weight: 800;
  font-size: 1rem;
}
.wkr-idname-tag { color: var(--muted); font-weight: 400; font-size: 0.81rem; }
/* Verified: this name is an account's, not something typed in for the evening. */
.wkr-p-check { color: var(--accent); font-weight: 900; margin-left: 0.25rem; }

/* Vehicle picker */
.wkr-picker {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid var(--panel-border);
  border-radius: 1rem;
  padding: 0.88rem;
}
.wkr-arrow {
  flex: 0 0 auto;
  width: 3rem;
  font-size: 1.38rem;
  border-radius: 0.75rem;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wkr-vcard { flex: 1; min-width: 0; text-align: center; }
.wkr-vemoji {
  font-size: 3.63rem;
  line-height: 1;
  margin-bottom: 0.25rem;
  filter: drop-shadow(0 0.5rem 1.13rem rgba(0, 0, 0, 0.55));
  animation: wkr-bob 3.4s ease-in-out infinite;
}
@keyframes wkr-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-0.31rem); }
}
.wkr-vname {
  font-size: 1.63rem;
  font-weight: 900;
  color: var(--accent);
  margin-bottom: 0.25rem;
}
.wkr-vdesc { color: var(--muted); font-size: 0.81rem; margin-bottom: 0.75rem; min-height: 2rem; }
.wkr-stats { display: grid; gap: 0.44rem; text-align: left; }
.wkr-stat { display: grid; grid-template-columns: 4.75rem 1fr; align-items: center; gap: 0.63rem; }
.wkr-stat-name {
  font-size: 0.69rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--muted);
  font-weight: 700;
}
.wkr-bar {
  height: 0.56rem;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 0.38rem;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.45);
}
.wkr-bar-fill {
  height: 100%;
  border-radius: 0.38rem;
  background: linear-gradient(90deg, var(--accent-3), var(--accent) 55%, var(--accent-2));
  box-shadow: 0 0 0.63rem rgba(255, 207, 51, 0.3);
  transition: width 0.24s cubic-bezier(0.2, 0.9, 0.3, 1);
}

/* Map selector */
.wkr-map {
  display: flex;
  align-items: stretch;
  gap: 0.75rem;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid var(--panel-border);
  border-radius: 1rem;
  padding: 0.88rem;
}
.wkr-map-card { flex: 1; min-width: 0; text-align: center; }
.wkr-map-emoji { font-size: 2.5rem; line-height: 1; margin-bottom: 0.25rem; }
.wkr-map-name {
  font-size: 1.38rem;
  font-weight: 900;
  color: var(--accent);
  margin-bottom: 0.25rem;
}
.wkr-map-desc { color: var(--muted); font-size: 0.81rem; margin-bottom: 0.5rem; min-height: 1.13rem; }
.wkr-map-diff {
  font-size: 0.94rem;
  letter-spacing: 0.13em;
  color: var(--accent-2);
}
.wkr-map-diff-label {
  font-size: 0.69rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--muted);
  font-weight: 700;
  margin-right: 0.38rem;
}
.wkr-map-hint {
  text-align: center;
  color: var(--muted);
  font-size: 0.75rem;
  margin-top: 0.38rem;
  font-style: italic;
}

/* Two-column body, used by the lobby. Everything in there is a short stack of
   its own, and a single column runs the panel off the bottom of a 1080p screen
   before the Start button — which is the one control that has to be reachable
   without scrolling. Collapses back to one column when there is no width for it. */
.wkr-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 1.25rem;
  align-items: start;
}
.wkr-cols > * { min-width: 0; }
@media (max-width: 700px), (pointer: coarse) {
  .wkr-cols { grid-template-columns: 1fr; gap: 0; }
}

/* Status / error */
.wkr-status { margin-top: 1rem; text-align: center; min-height: 1.25rem; font-size: 0.88rem; color: var(--muted); }
.wkr-error { color: #ff8a8a; font-weight: 700; }

/* Lobby */
.wkr-code-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.88rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px dashed var(--panel-border);
  border-radius: 1rem;
  padding: 0.6rem 1.13rem;
  margin-bottom: 0.25rem;
}
.wkr-code-big {
  font-size: 2.9rem;
  font-weight: 900;
  letter-spacing: 0.5rem;
  font-variant-numeric: tabular-nums;
  color: var(--accent);
  text-shadow: 0 2px 1rem rgba(255, 207, 51, 0.35), 0 0 2.75rem rgba(255, 207, 51, 0.2);
}
.wkr-hint { text-align: center; color: var(--muted); font-size: 0.81rem; margin-bottom: 0.75rem; }
/* Share link. The code is the thing to read out loud; this is the thing to paste
   into a chat, so it gets its own row rather than competing with the big code. */
.wkr-share {
  display: flex;
  align-items: center;
  gap: 0.63rem;
  background: rgba(0, 0, 0, 0.28);
  border: 1px solid var(--panel-border);
  border-radius: 0.75rem;
  padding: 0.38rem 0.38rem 0.38rem 0.75rem;
  margin-bottom: 0.5rem;
}
.wkr-share-url {
  flex: 1;
  min-width: 0;
  font-size: 0.81rem;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* A URL is for copying, so make selecting it easy rather than dragging it. */
  user-select: all;
}

/* Stepper: one control that says "how many", where a row of numbers used to sit.
   The value is the anchor, so it holds a fixed width — the buttons must not
   shuffle sideways as the label goes from "None" to "7 bots". */
.wkr-step {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}
.wkr-step .wkr-step-btn {
  flex: 0 0 auto;
  width: 2.75rem;
  font-size: 1.25rem;
  font-weight: 900;
  padding: 0.44rem 0;
  line-height: 1.2;
}
.wkr-step-value {
  flex: 0 1 8rem;
  text-align: center;
  font-weight: 800;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}
.wkr-step-value.wkr-step-off { color: var(--muted); font-weight: 700; }
/* The roster scrolls rather than stretching the panel. A full grid of eight is
   several rows taller than a solo lobby, which on a laptop pushes Start Race off
   the bottom of the screen — the panel would grow exactly when the lobby got
   busy enough to be worth looking at. A little over four rows are shown, so it
   is visibly cut off and obvious there is more below. */
.wkr-players {
  display: grid;
  gap: 0.44rem;
  margin: 0.38rem 0 0.5rem;
  max-height: 12.4rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  /* Reserve the gutter so rows do not reflow when the scrollbar appears. */
  padding-right: 0.3rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.22) transparent;
}
.wkr-players::-webkit-scrollbar { width: 0.5rem; }
.wkr-players::-webkit-scrollbar-track { background: transparent; }
.wkr-players::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.22);
  border-radius: 999px;
}
.wkr-players::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.35); }
.wkr-player {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--panel-border);
  border-radius: 0.75rem;
  padding: 0.5rem 0.8rem;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.wkr-player:hover { background: rgba(255, 255, 255, 0.07); }
.wkr-player.wkr-me {
  border-color: var(--accent);
  background: rgba(255, 207, 51, 0.09);
  box-shadow: 0 0 0 1px rgba(255, 207, 51, 0.12), 0 0.38rem 1.13rem rgba(255, 207, 51, 0.08);
}
.wkr-p-emoji { font-size: 1.63rem; filter: drop-shadow(0 0.19rem 0.38rem rgba(0, 0, 0, 0.5)); }
.wkr-p-name { flex: 1; font-weight: 700; font-size: 1rem; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.wkr-p-tag { color: var(--muted); font-weight: 400; font-size: 0.81rem; }
.wkr-badge {
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.31rem 0.63rem;
  border-radius: 999px;
  white-space: nowrap;
}
.wkr-badge-ready { background: rgba(93, 255, 150, 0.16); color: #82f5a8; }
.wkr-badge-notready { background: rgba(255, 255, 255, 0.08); color: var(--muted); }
.wkr-p-off { opacity: 0.45; }
.wkr-row { display: flex; gap: 0.63rem; margin-top: 0.88rem; }
.wkr-row .wkr-btn { flex: 1; }

/* Results */
.wkr-headline {
  text-align: center;
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 1.13rem;
  color: var(--accent);
}
.wkr-table { width: 100%; border-collapse: collapse; margin-bottom: 0.38rem; }
.wkr-table th {
  text-align: left;
  font-size: 0.69rem;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--muted);
  padding: 0.38rem 0.63rem;
  border-bottom: 1px solid var(--panel-border);
}
.wkr-table td { padding: 0.63rem; border-bottom: 1px solid rgba(255, 255, 255, 0.06); font-size: 0.94rem; }
.wkr-table tr.wkr-me td { color: var(--accent); font-weight: 800; }
.wkr-t-place { font-weight: 900; width: 2.75rem; }
.wkr-t-emoji { width: 2.13rem; font-size: 1.25rem; }
.wkr-t-time { text-align: right; font-variant-numeric: tabular-nums; }

/* Leaderboard */
.wkr-panel-wide { width: min(47.5rem, 96vw); }
.wkr-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin: 0.25rem 0 1.13rem;
}
.wkr-filter {
  font-size: 0.81rem;
  padding: 0.56rem 0.88rem;
}
.wkr-filter.wkr-filter-active {
  color: #10131f;
  background: linear-gradient(90deg, var(--accent), var(--accent-2));
  border: 1px solid transparent;
  box-shadow: 0 0.5rem 1.38rem rgba(255, 93, 162, 0.28);
}
/* Segmented choosers (bot count, bot skill, graphics preset). Buttons size to
   their content rather than stretching to fill: in a half-width column the bot
   count wraps, and a stretched-to-fill last row leaves one enormous "7" sitting
   under seven small ones. */
.wkr-seg { display: flex; gap: 0.44rem; flex-wrap: wrap; justify-content: center; }
.wkr-seg .wkr-btn { flex: 0 1 auto; min-width: 2rem; font-size: 0.88rem; padding: 0.63rem 0.6rem; }
.wkr-seg-active {
  color: #100f18;
  background: linear-gradient(100deg, var(--accent), var(--accent-2));
  border: 1px solid transparent;
  box-shadow: 0 0.5rem 1.38rem rgba(255, 93, 162, 0.28);
}
.wkr-badge-bot { background: rgba(120, 200, 255, 0.16); color: #8fd4ff; }

/* Sign-in screen. Two tabs over one form: the fields are the same either way,
   and a second screen for "create account" would only be the same form again. */
.wkr-tabs {
  display: flex;
  gap: 0.44rem;
  margin: 0.25rem 0 0.5rem;
}
.wkr-tabs .wkr-btn { flex: 1; }
.wkr-lb-mine td { color: var(--accent); font-weight: 800; }

.wkr-lb-empty {
  text-align: center;
  color: var(--muted);
  font-size: 1rem;
  padding: 2.13rem 0.63rem;
  font-style: italic;
}
.wkr-lb-track { width: 9.4rem; }
.wkr-lb-track-emoji { margin-right: 0.38rem; }

/* Phones: tighten everything up and respect the notch. Sizes are still in rem,
   so this only changes the proportions the narrow layout needs — the overall
   scale comes from the root font size in style.css. */
@media (max-width: 700px), (pointer: coarse) {
  .wkr-overlay {
    padding: calc(0.75rem + env(safe-area-inset-top)) 0.75rem calc(0.75rem + env(safe-area-inset-bottom));
  }
  .wkr-panel { padding: 1.25rem 1.13rem 1.38rem; border-radius: 1.13rem; }
  .wkr-title { font-size: 2.5rem; }
  .wkr-subtitle { font-size: 0.81rem; margin-bottom: 0.88rem; }
  .wkr-vemoji { font-size: 2.75rem; }
  .wkr-vname { font-size: 1.31rem; }
  .wkr-vdesc { font-size: 0.75rem; min-height: 0; }
  .wkr-picker, .wkr-map { padding: 0.63rem; gap: 0.5rem; }
  .wkr-arrow { width: 2.38rem; font-size: 1.13rem; }
  .wkr-stat { grid-template-columns: 3.88rem 1fr; }
  .wkr-code-big { font-size: 2.5rem; letter-spacing: 0.31rem; }
  .wkr-headline { font-size: 1.9rem; }
  .wkr-row { flex-wrap: wrap; }
  .wkr-seg .wkr-btn { min-width: 3.25rem; font-size: 0.81rem; padding: 0.56rem 0.5rem; }
  .wkr-table td, .wkr-table th { padding: 0.44rem 0.38rem; font-size: 0.81rem; }
  .wkr-lb-track { width: auto; }
  /* Bigger hit targets for the one control you tap repeatedly. */
  .wkr-step .wkr-step-btn { width: 3.25rem; padding: 0.56rem 0; }
  .wkr-share { padding-left: 0.63rem; }
  .wkr-share-url { font-size: 0.75rem; }
}
`;

export class UI {
  private readonly root: HTMLElement;
  private readonly vehicles: VehicleStats[];
  private readonly tracks: TrackInfo[];
  private readonly callbacks: UICallbacks;

  private readonly container: HTMLDivElement;
  private readonly defaultName: string;

  /** Screen currently on show, so a re-render of it can be made invisible. */
  private currentScreen: ScreenId | null = null;
  /** Screen being built right now; read by reset() to decide on the animation. */
  private pendingScreen: ScreenId | null = null;

  /** Currently selected vehicle index into `vehicles`. */
  private vehicleIndex = 0;
  /** Persisted name typed by the user (survives re-renders). */
  private nameValue: string;
  /** Persisted join-code text (survives re-renders). */
  private codeValue = '';
  /** Current graphics preset, mirrored from the game for the menu highlight. */
  private quality: QualityLevel = 'high';

  /** Signed-in account name, or null while racing as a guest. */
  private account: string | null = null;
  /** Sign-in screen state, all of it persisted across re-renders. */
  private authMode: AuthMode = 'login';
  private authName = '';
  private authPassword = '';
  /** True while a sign-in attempt is in flight, so the button can't be spammed. */
  private authBusy = false;

  /** The live text inputs so isInputActive() can consult activeElement. */
  private readonly textInputs = new Set<HTMLInputElement>();

  constructor(
    root: HTMLElement,
    vehicles: VehicleStats[],
    tracks: TrackInfo[],
    callbacks: UICallbacks,
  ) {
    this.root = root;
    this.vehicles = vehicles.length > 0 ? vehicles.slice() : [];
    this.tracks = tracks.length > 0 ? tracks.slice() : [];
    this.callbacks = callbacks;
    this.defaultName = randomName();
    this.nameValue = this.defaultName;

    UI.injectStyle();

    this.container = document.createElement('div');
    this.container.className = 'wkr-overlay';
    this.container.style.display = 'none';
    this.root.appendChild(this.container);
  }

  private static injectStyle(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  // ---- public API -------------------------------------------------------

  showMainMenu(): void {
    this.render('menu', () => this.renderMainMenu());
  }

  showLobby(lobby: LobbyInfo, myId: string): void {
    this.render('lobby', () => this.renderLobby(lobby, myId));
  }

  /**
   * Repaint the lobby only if that is what the player is looking at.
   *
   * The roster changes on every ready-up, bot step, map change and join, and each
   * one arrives as a fresh lobby to draw. Drawing it unconditionally throws
   * anyone who opened the leaderboard or the sign-in form straight back out —
   * mid-password, at whatever moment someone else happened to ready up. Coming
   * back out of either renders the lobby from the caller's latest state, so
   * nothing is lost by ignoring the push while one is up.
   */
  refreshLobby(lobby: LobbyInfo, myId: string): void {
    if (this.currentScreen === 'auth' || this.currentScreen === 'leaderboard') return;
    this.showLobby(lobby, myId);
  }

  showResults(lobby: LobbyInfo, myId: string): void {
    this.render('results', () => this.renderResults(lobby, myId));
  }

  showLeaderboard(
    trackId: string | null,
    entries: LeaderboardEntry[],
    tracks: TrackInfo[],
  ): void {
    this.render('leaderboard', () => this.renderLeaderboard(trackId, entries, tracks));
  }

  showAuth(): void {
    this.authBusy = false;
    this.render('auth', () => this.renderAuth());
  }

  hide(): void {
    this.container.style.display = 'none';
  }

  /**
   * Who the player is signed in as, or null for a guest. Prefilling the sign-in
   * form with the account name makes signing back in a one-field job.
   */
  setAccount(account: string | null): void {
    this.account = account;
    this.authBusy = false;
    this.authPassword = '';
    if (account) this.authName = account;
  }

  /** True when the player is signed in, i.e. when their times count. */
  get signedIn(): boolean {
    return this.account !== null;
  }

  /** Show a refused sign-in on the auth screen and re-enable the button. */
  setAuthError(message: string): void {
    this.authBusy = false;
    const submit = this.container.querySelector<HTMLButtonElement>('.wkr-auth-submit');
    if (submit) submit.disabled = false;
    this.setError(message);
  }

  /**
   * Rebuild a screen, and make a rebuild of the SAME screen invisible.
   *
   * Screens are re-rendered wholesale, which is simple and keeps the DOM honest
   * about the server's state — but the lobby repaints on every ready-up, bot
   * change, map change and join, and a wholesale rebuild announces itself: the
   * panel replays its entrance animation, the scroll position jumps back to the
   * top and anything focused loses focus mid-keystroke. It reads as the page
   * reloading itself every time anyone touches anything.
   *
   * So: same screen as last time means no entrance animation, and the scroll
   * offsets and text-input focus are carried across the rebuild.
   */
  private render(screen: ScreenId, build: () => void): void {
    const same = screen === this.currentScreen;
    const scroll = same ? this.captureScroll() : null;
    const focus = same ? this.captureFocus() : null;

    this.pendingScreen = screen;
    build();
    this.currentScreen = screen;
    this.container.style.display = 'flex';

    if (scroll) this.restoreScroll(scroll);
    if (focus) this.restoreFocus(focus);
  }

  /** Scroll offsets that must survive a rebuild. */
  private captureScroll(): ScrollState {
    return {
      overlay: this.container.scrollTop,
      players: this.container.querySelector<HTMLElement>('.wkr-players')?.scrollTop ?? 0,
    };
  }

  private restoreScroll(scroll: ScrollState): void {
    this.container.scrollTop = scroll.overlay;
    const players = this.container.querySelector<HTMLElement>('.wkr-players');
    if (players) players.scrollTop = scroll.players;
  }

  /** Which input was focused, and where the caret was in it. */
  private captureFocus(): FocusState | null {
    const active = document.activeElement;
    if (!(active instanceof HTMLInputElement) || !this.textInputs.has(active)) return null;
    const field = active.dataset.field;
    if (!field) return null;
    return { field, start: active.selectionStart ?? 0, end: active.selectionEnd ?? 0 };
  }

  private restoreFocus(focus: FocusState): void {
    const input = this.container.querySelector<HTMLInputElement>(
      `input[data-field="${focus.field}"]`,
    );
    if (!input) return;
    input.focus();
    try {
      input.setSelectionRange(focus.start, focus.end);
    } catch {
      /* not every input type carries a selection; focus alone is the point */
    }
  }

  setStatus(msg: string): void {
    const el = this.container.querySelector<HTMLDivElement>('.wkr-status');
    if (el) {
      el.className = 'wkr-status';
      el.textContent = msg;
    }
  }

  setError(msg: string): void {
    const el = this.container.querySelector<HTMLDivElement>('.wkr-status');
    if (el) {
      el.className = 'wkr-status wkr-error';
      el.textContent = msg;
    }
  }

  /** The name the player will race under, after trimming and fallback. */
  get playerName(): string {
    return this.resolvedName();
  }

  /** The vehicle currently showing in the picker. */
  get selectedVehicle(): string {
    return this.selectedVehicleId();
  }

  isInputActive(): boolean {
    const active = document.activeElement;
    for (const input of this.textInputs) {
      if (input === active) return true;
    }
    return false;
  }

  // ---- rendering helpers ------------------------------------------------

  /** Reset the container and forget any tracked inputs from the old render. */
  private reset(wide = false): HTMLDivElement {
    this.textInputs.clear();
    this.container.textContent = '';
    const panel = document.createElement('div');
    panel.className = wide ? 'wkr-panel wkr-panel-wide' : 'wkr-panel';
    // Only a screen the player has just arrived at should animate in.
    if (this.pendingScreen === this.currentScreen) panel.classList.add('wkr-instant');
    this.container.appendChild(panel);
    return panel;
  }

  private trackInput(input: HTMLInputElement): void {
    this.textInputs.add(input);
  }

  private currentVehicle(): VehicleStats | null {
    if (this.vehicles.length === 0) return null;
    if (this.vehicleIndex < 0 || this.vehicleIndex >= this.vehicles.length) {
      this.vehicleIndex = 0;
    }
    return this.vehicles[this.vehicleIndex];
  }

  private selectedVehicleId(): string {
    return this.currentVehicle()?.id ?? '';
  }

  private vehicleById(id: string): VehicleStats | null {
    return this.vehicles.find((v) => v.id === id) ?? null;
  }

  private trackById(id: string): TrackInfo | null {
    return this.tracks.find((t) => t.id === id) ?? null;
  }

  /** Build a reusable vehicle picker. `onChange` fires after each cycle. */
  private buildPicker(onChange: (vehicleId: string) => void): HTMLElement {
    const picker = document.createElement('div');
    picker.className = 'wkr-picker';

    const left = document.createElement('button');
    left.className = 'wkr-btn wkr-btn-ghost wkr-arrow';
    left.type = 'button';
    left.textContent = '◀';
    left.setAttribute('aria-label', 'Previous vehicle');

    const card = document.createElement('div');
    card.className = 'wkr-vcard';

    const right = document.createElement('button');
    right.className = 'wkr-btn wkr-btn-ghost wkr-arrow';
    right.type = 'button';
    right.textContent = '▶';
    right.setAttribute('aria-label', 'Next vehicle');

    const renderCard = (): void => {
      const v = this.currentVehicle();
      card.textContent = '';
      if (!v) {
        card.textContent = 'No vehicles available';
        return;
      }

      const emoji = document.createElement('div');
      emoji.className = 'wkr-vemoji';
      emoji.textContent = v.emoji;

      const name = document.createElement('div');
      name.className = 'wkr-vname';
      name.textContent = v.name;

      const desc = document.createElement('div');
      desc.className = 'wkr-vdesc';
      desc.textContent = v.description;

      const stats = document.createElement('div');
      stats.className = 'wkr-stats';
      // Rough normalisation maxima so bars read sensibly across vehicles.
      stats.appendChild(this.buildStat('Speed', statPct(v.maxSpeed, 60)));
      stats.appendChild(this.buildStat('Accel', statPct(v.accel, 45)));
      stats.appendChild(this.buildStat('Handling', statPct(v.handling, 4)));
      stats.appendChild(this.buildStat('Grip', statPct(v.grip, 1)));

      card.append(emoji, name, desc, stats);
    };

    const cycle = (delta: number): void => {
      if (this.vehicles.length === 0) return;
      this.vehicleIndex =
        (this.vehicleIndex + delta + this.vehicles.length) % this.vehicles.length;
      renderCard();
      onChange(this.selectedVehicleId());
    };

    left.addEventListener('click', () => cycle(-1));
    right.addEventListener('click', () => cycle(1));

    renderCard();
    picker.append(left, card, right);
    return picker;
  }

  private buildStat(label: string, pct: number): HTMLElement {
    const row = document.createElement('div');
    row.className = 'wkr-stat';

    const name = document.createElement('div');
    name.className = 'wkr-stat-name';
    name.textContent = label;

    const bar = document.createElement('div');
    bar.className = 'wkr-bar';
    const fill = document.createElement('div');
    fill.className = 'wkr-bar-fill';
    fill.style.width = `${pct}%`;
    bar.appendChild(fill);

    row.append(name, bar);
    return row;
  }

  private buildStatusArea(): HTMLDivElement {
    const status = document.createElement('div');
    status.className = 'wkr-status';
    return status;
  }

  /**
   * A row of mutually exclusive buttons. Used for the bot count and the
   * graphics preset, which are both "pick one of a handful".
   */
  private buildSegmented<T>(
    values: T[],
    label: (value: T) => string,
    isActive: (value: T) => boolean,
    onPick: (value: T) => void,
    enabled = true,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'wkr-seg';
    for (const value of values) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = isActive(value)
        ? 'wkr-btn wkr-btn-ghost wkr-seg-active'
        : 'wkr-btn wkr-btn-ghost';
      btn.textContent = label(value);
      btn.disabled = !enabled;
      btn.addEventListener('click', () => onPick(value));
      row.appendChild(btn);
    }
    return row;
  }

  /**
   * A − / value / + counter for a number with a small range.
   *
   * Nicer than a row of numbers once there are more than a handful: one place to
   * read the answer, and two targets that stay put instead of eight that move
   * about as the row rewraps.
   *
   * The count is stepped locally as well as sent, so a quick double-tap lands on
   * +2 rather than +1 twice — the authoritative value only comes back a round
   * trip later, and the next re-render adopts it.
   */
  private buildStepper(
    value: number,
    min: number,
    max: number,
    label: (n: number) => string,
    onChange: (n: number) => void,
    enabled = true,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'wkr-step';

    let current = Math.max(min, Math.min(max, value));

    const makeButton = (text: string, aria: string, delta: number): HTMLButtonElement => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'wkr-btn wkr-btn-ghost wkr-step-btn';
      btn.textContent = text;
      btn.setAttribute('aria-label', aria);
      btn.addEventListener('click', () => step(delta));
      return btn;
    };

    const minus = makeButton('−', `Fewer (${label(min)} minimum)`, -1);
    const plus = makeButton('+', `More (${label(max)} maximum)`, 1);

    const readout = document.createElement('div');
    readout.setAttribute('aria-live', 'polite');

    const paint = (): void => {
      readout.className =
        current === min ? 'wkr-step-value wkr-step-off' : 'wkr-step-value';
      readout.textContent = label(current);
      minus.disabled = !enabled || current <= min;
      plus.disabled = !enabled || current >= max;
    };

    const step = (delta: number): void => {
      const next = Math.max(min, Math.min(max, current + delta));
      if (next === current) return;
      current = next;
      paint();
      onChange(current);
    };

    paint();
    row.append(minus, readout, plus);
    return row;
  }

  /** Remember the current graphics preset so the menu can highlight it. */
  setQuality(level: QualityLevel): void {
    this.quality = level;
  }

  /**
   * Build the lobby's map section: a card showing the resolved track. When
   * `editable` (host), ◀/▶ arrows cycle `this.tracks` and call onSelectTrack.
   * Otherwise it renders read-only with a "chosen by host" hint.
   */
  private buildMapSelector(currentTrackId: string, editable: boolean): HTMLElement {
    const wrap = document.createElement('div');

    const box = document.createElement('div');
    box.className = 'wkr-map';

    const card = document.createElement('div');
    card.className = 'wkr-map-card';

    const renderCard = (): void => {
      const info = this.trackById(currentTrackId) ?? this.tracks[0] ?? null;
      card.textContent = '';
      if (!info) {
        card.textContent = 'No maps available';
        return;
      }

      const emoji = document.createElement('div');
      emoji.className = 'wkr-map-emoji';
      emoji.textContent = info.emoji;

      const name = document.createElement('div');
      name.className = 'wkr-map-name';
      name.textContent = info.name;

      const desc = document.createElement('div');
      desc.className = 'wkr-map-desc';
      desc.textContent = info.description;

      const diff = document.createElement('div');
      diff.className = 'wkr-map-diff';
      const diffLabel = document.createElement('span');
      diffLabel.className = 'wkr-map-diff-label';
      diffLabel.textContent = 'Difficulty';
      const stars = document.createElement('span');
      stars.textContent = difficultyStars(info.difficulty);
      diff.append(diffLabel, stars);

      card.append(emoji, name, desc, diff);
    };

    renderCard();

    if (editable && this.tracks.length > 0) {
      const left = document.createElement('button');
      left.className = 'wkr-btn wkr-btn-ghost wkr-arrow';
      left.type = 'button';
      left.textContent = '◀';
      left.setAttribute('aria-label', 'Previous map');

      const right = document.createElement('button');
      right.className = 'wkr-btn wkr-btn-ghost wkr-arrow';
      right.type = 'button';
      right.textContent = '▶';
      right.setAttribute('aria-label', 'Next map');

      const cycle = (delta: number): void => {
        if (this.tracks.length === 0) return;
        let idx = this.tracks.findIndex((t) => t.id === currentTrackId);
        if (idx < 0) idx = 0;
        const next =
          this.tracks[(idx + delta + this.tracks.length) % this.tracks.length];
        this.callbacks.onSelectTrack(next.id);
      };

      left.addEventListener('click', () => cycle(-1));
      right.addEventListener('click', () => cycle(1));

      box.append(left, card, right);
      wrap.append(box);
    } else {
      box.append(card);
      wrap.append(box);

      const hint = document.createElement('div');
      hint.className = 'wkr-map-hint';
      hint.textContent = 'Map chosen by host';
      wrap.append(hint);
    }

    return wrap;
  }

  // ---- Main menu --------------------------------------------------------

  /**
   * The "who am I" strip: signed in as somebody, or racing as a guest with the
   * way in alongside. Guests are told what they are missing exactly once, here,
   * rather than being nagged on every screen.
   */
  private buildAccountRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'wkr-account';

    const who = document.createElement('div');
    who.className = 'wkr-account-who';

    if (this.account) {
      who.append(document.createTextNode('👤 Signed in as '));
      const name = document.createElement('span');
      name.className = 'wkr-account-name';
      name.textContent = this.account;
      who.append(name);

      const out = document.createElement('button');
      out.className = 'wkr-btn wkr-btn-ghost wkr-btn-sm';
      out.type = 'button';
      out.textContent = 'Sign out';
      out.addEventListener('click', () => this.callbacks.onLogout());

      row.append(who, out);
      return row;
    }

    who.textContent = '👤 Racing as a guest — times are not saved';

    const inBtn = document.createElement('button');
    inBtn.className = 'wkr-btn wkr-btn-sm';
    inBtn.type = 'button';
    inBtn.textContent = 'Sign in';
    inBtn.addEventListener('click', () => this.callbacks.onShowAuth());

    row.append(who, inBtn);
    return row;
  }

  private renderMainMenu(): void {
    const panel = this.reset();

    const accountRow = this.buildAccountRow();

    const title = document.createElement('div');
    title.className = 'wkr-title';
    const titleEmoji = document.createElement('span');
    titleEmoji.className = 'wkr-title-emoji';
    titleEmoji.textContent = '🏁';
    title.append(titleEmoji, document.createTextNode('Weird Kart Racer'));

    const subtitle = document.createElement('div');
    subtitle.className = 'wkr-subtitle';
    subtitle.textContent = 'Pick a ridiculous ride and race your friends.';

    const nameLabel = document.createElement('label');
    nameLabel.className = 'wkr-label';
    nameLabel.textContent = this.account ? 'You race as' : 'Your name';

    // Signed in: the name is the account's, and not up for editing here.
    const nameField = this.account
      ? this.buildAccountName(this.account)
      : this.buildNameInput();

    const vehicleLabel = document.createElement('label');
    vehicleLabel.className = 'wkr-label';
    vehicleLabel.textContent = 'Choose your ride';

    const picker = this.buildPicker(() => {
      /* selection persisted via vehicleIndex; no server call in the menu */
    });

    const createBtn = document.createElement('button');
    createBtn.className = 'wkr-btn wkr-btn-block wkr-btn-big';
    createBtn.type = 'button';
    createBtn.style.marginTop = '18px';
    createBtn.textContent = 'Create Lobby';
    createBtn.addEventListener('click', () => {
      this.callbacks.onCreateLobby(this.resolvedName(), this.selectedVehicleId());
    });

    const joinLabel = document.createElement('label');
    joinLabel.className = 'wkr-label';
    joinLabel.textContent = 'Join a lobby';

    const joinRow = document.createElement('div');
    joinRow.className = 'wkr-join-row';

    const codeInput = document.createElement('input');
    codeInput.className = 'wkr-input wkr-code-input';
    codeInput.type = 'text';
    codeInput.maxLength = 5;
    codeInput.placeholder = 'CODE';
    codeInput.value = this.codeValue;
    codeInput.autocomplete = 'off';
    codeInput.dataset.field = 'code';
    codeInput.addEventListener('input', () => {
      codeInput.value = codeInput.value.toUpperCase();
      this.codeValue = codeInput.value;
    });
    codeInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') this.attemptJoin();
    });
    this.trackInput(codeInput);

    const joinBtn = document.createElement('button');
    joinBtn.className = 'wkr-btn';
    joinBtn.type = 'button';
    joinBtn.textContent = 'Join';
    joinBtn.addEventListener('click', () => this.attemptJoin());

    joinRow.append(codeInput, joinBtn);

    const leaderboardBtn = document.createElement('button');
    leaderboardBtn.className = 'wkr-btn wkr-btn-ghost wkr-btn-block';
    leaderboardBtn.type = 'button';
    leaderboardBtn.style.marginTop = '18px';
    leaderboardBtn.textContent = '🏆 Leaderboard';
    leaderboardBtn.addEventListener('click', () => {
      this.callbacks.onShowLeaderboard(null);
    });

    const qualityLabel = document.createElement('label');
    qualityLabel.className = 'wkr-label';
    qualityLabel.textContent = 'Graphics';

    const qualityRow = this.buildSegmented(
      QUALITY_LEVELS,
      (level) => QUALITY_LABELS[level],
      (level) => level === this.quality,
      (level) => {
        this.quality = level;
        this.callbacks.onSelectQuality(level);
        // Repaint so the new choice is highlighted — through render(), so it
        // keeps the scroll position and anything half-typed.
        this.render('menu', () => this.renderMainMenu());
      },
    );

    const controls = document.createElement('div');
    controls.className = 'wkr-hint';
    controls.style.marginTop = '16px';
    controls.style.marginBottom = '0';
    controls.textContent =
      'WASD / Arrows drive · Space boosts · Shift drifts · M mutes — gamepad and touch also work';

    const status = this.buildStatusArea();

    panel.append(
      accountRow,
      title,
      subtitle,
      nameLabel,
      nameField,
      vehicleLabel,
      picker,
      createBtn,
      joinLabel,
      joinRow,
      leaderboardBtn,
      qualityLabel,
      qualityRow,
      controls,
      status,
    );
  }

  /** The free-text name box a guest races under. */
  private buildNameInput(): HTMLElement {
    const input = document.createElement('input');
    input.className = 'wkr-input';
    input.type = 'text';
    input.maxLength = 20;
    input.placeholder = this.defaultName;
    input.value = this.nameValue;
    input.dataset.field = 'name';
    input.addEventListener('input', () => {
      this.nameValue = input.value;
    });
    this.trackInput(input);
    return input;
  }

  /** The fixed name a signed-in player races under. */
  private buildAccountName(account: string): HTMLElement {
    const box = document.createElement('div');
    box.className = 'wkr-idname';
    box.append(document.createTextNode(`👤 ${account}`));
    const tag = document.createElement('span');
    tag.className = 'wkr-idname-tag';
    tag.textContent = 'your account name';
    box.append(tag);
    return box;
  }

  /**
   * A signed-in player always races under their account name — the alternative
   * is a kart labelled one thing and a leaderboard row saying another. Guests
   * race under whatever they typed, or a random name if they typed nothing.
   */
  private resolvedName(): string {
    if (this.account) return this.account;
    const trimmed = this.nameValue.trim();
    return trimmed.length > 0 ? trimmed : this.defaultName;
  }

  /**
   * Prefill the join box — used when a `?join=` link is opened, so a code that
   * fails to connect is still sitting there to retry rather than lost with the
   * URL that carried it.
   */
  setJoinCode(code: string): void {
    this.codeValue = code.toUpperCase();
    const input = this.container.querySelector<HTMLInputElement>('input[data-field="code"]');
    if (input) input.value = this.codeValue;
  }

  private attemptJoin(): void {
    const code = this.codeValue.trim().toUpperCase();
    if (code.length === 0) {
      this.setError('Enter a lobby code to join.');
      return;
    }
    this.callbacks.onJoinLobby(code, this.resolvedName(), this.selectedVehicleId());
  }

  // ---- Sign in ----------------------------------------------------------

  /**
   * Sign in or create an account. Both are the same two fields, so they are two
   * tabs over one form rather than two screens.
   *
   * The form is checked here before anything is sent — the same shared rules the
   * server applies (`accountNameError` / `passwordError`), so "too short" comes
   * back instantly instead of after a round trip and an argon2 hash.
   */
  private renderAuth(): void {
    const panel = this.reset();
    const registering = this.authMode === 'register';

    const title = document.createElement('div');
    title.className = 'wkr-title';
    const titleEmoji = document.createElement('span');
    titleEmoji.className = 'wkr-title-emoji';
    titleEmoji.textContent = '🏆';
    title.append(
      titleEmoji,
      document.createTextNode(registering ? 'Create account' : 'Sign in'),
    );

    const subtitle = document.createElement('div');
    subtitle.className = 'wkr-subtitle';
    subtitle.textContent =
      'An account saves your best times and puts you on the global leaderboard. Racing works without one.';

    const tabs = document.createElement('div');
    tabs.className = 'wkr-tabs';
    const makeTab = (label: string, mode: AuthMode): HTMLButtonElement => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        mode === this.authMode
          ? 'wkr-btn wkr-btn-ghost wkr-seg-active'
          : 'wkr-btn wkr-btn-ghost';
      btn.textContent = label;
      btn.addEventListener('click', () => {
        if (this.authMode === mode) return;
        this.authMode = mode;
        this.render('auth', () => this.renderAuth());
      });
      return btn;
    };
    tabs.append(makeTab('Sign in', 'login'), makeTab('Create account', 'register'));

    const nameLabel = document.createElement('label');
    nameLabel.className = 'wkr-label';
    nameLabel.textContent = 'Account name';

    const nameInput = document.createElement('input');
    nameInput.className = 'wkr-input';
    nameInput.type = 'text';
    nameInput.maxLength = ACCOUNT_NAME_MAX;
    nameInput.placeholder = 'TurboBanana';
    nameInput.value = this.authName;
    nameInput.autocomplete = 'username';
    nameInput.dataset.field = 'authName';
    nameInput.addEventListener('input', () => {
      this.authName = nameInput.value;
    });
    this.trackInput(nameInput);

    const passLabel = document.createElement('label');
    passLabel.className = 'wkr-label';
    passLabel.textContent = 'Password';

    const passInput = document.createElement('input');
    passInput.className = 'wkr-input';
    passInput.type = 'password';
    passInput.maxLength = PASSWORD_MAX;
    passInput.placeholder = '••••••••';
    passInput.value = this.authPassword;
    passInput.autocomplete = registering ? 'new-password' : 'current-password';
    passInput.dataset.field = 'authPassword';
    passInput.addEventListener('input', () => {
      this.authPassword = passInput.value;
    });
    this.trackInput(passInput);

    const submit = document.createElement('button');
    submit.className = 'wkr-btn wkr-btn-block wkr-btn-big wkr-auth-submit';
    submit.type = 'button';
    submit.style.marginTop = '18px';
    submit.textContent = registering ? 'Create account' : 'Sign in';
    submit.disabled = this.authBusy;
    submit.addEventListener('click', () => this.submitAuth(submit));

    for (const input of [nameInput, passInput]) {
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this.submitAuth(submit);
      });
    }

    const rules = document.createElement('div');
    rules.className = 'wkr-hint';
    rules.style.marginTop = '0.5rem';
    rules.textContent = registering
      ? '3–16 characters, letters, numbers, - and _ · password at least 6 characters'
      : 'Signed in on this browser until you sign out.';

    const back = document.createElement('button');
    back.className = 'wkr-btn wkr-btn-ghost wkr-btn-block';
    back.type = 'button';
    back.textContent = 'Back';
    back.addEventListener('click', () => this.callbacks.onCloseAuth());

    const status = this.buildStatusArea();

    panel.append(
      title,
      subtitle,
      tabs,
      nameLabel,
      nameInput,
      passLabel,
      passInput,
      submit,
      rules,
      back,
      status,
    );
  }

  /** Validate what was typed, then hand it to the server. */
  private submitAuth(submit: HTMLButtonElement): void {
    if (this.authBusy) return;

    const name = this.authName.trim();
    const problem = accountNameError(name) ?? passwordError(this.authPassword);
    if (problem) {
      this.setError(problem);
      return;
    }

    this.authBusy = true;
    submit.disabled = true;
    this.setStatus(this.authMode === 'register' ? 'Creating account…' : 'Signing in…');

    if (this.authMode === 'register') {
      this.callbacks.onRegister(name, this.authPassword);
    } else {
      this.callbacks.onLogin(name, this.authPassword);
    }
  }

  // ---- Lobby ------------------------------------------------------------

  private renderLobby(lobby: LobbyInfo, myId: string): void {
    // Wide panel: the lobby carries the most settings of any screen, and laying
    // them out in two columns is what keeps Start Race above the fold.
    const panel = this.reset(true);

    const me = lobby.players.find((p) => p.id === myId) ?? null;
    const iAmHost = me?.isHost ?? lobby.hostId === myId;

    const title = document.createElement('div');
    title.className = 'wkr-title';
    title.textContent = 'Lobby';

    // Sync the selected picker index to the player's server-side vehicle so the
    // picker opens on the right ride.
    if (me) {
      const idx = this.vehicles.findIndex((v) => v.id === me.vehicleId);
      if (idx >= 0) this.vehicleIndex = idx;
    }

    // Code box + copy.
    const codeBox = document.createElement('div');
    codeBox.className = 'wkr-code-box';

    const codeBig = document.createElement('div');
    codeBig.className = 'wkr-code-big';
    codeBig.textContent = lobby.code;

    const copyBtn = document.createElement('button');
    copyBtn.className = 'wkr-btn wkr-btn-ghost';
    copyBtn.type = 'button';
    copyBtn.textContent = 'Copy';
    copyBtn.addEventListener('click', () => this.copyText(lobby.code, copyBtn));

    codeBox.append(codeBig, copyBtn);

    const hint = document.createElement('div');
    hint.className = 'wkr-hint';
    hint.style.marginBottom = '0.5rem';
    hint.textContent = 'Read out the code, or send the link:';

    const share = this.buildShareRow(lobby.code);

    // Player list.
    const list = document.createElement('div');
    list.className = 'wkr-players';
    for (const p of lobby.players) {
      list.appendChild(this.buildPlayerRow(p, myId));
    }

    // Map section — reflects lobby.trackId; host can cycle it.
    const mapLabel = document.createElement('label');
    mapLabel.className = 'wkr-label';
    mapLabel.textContent = 'Map';

    const mapSelector = this.buildMapSelector(lobby.trackId, iAmHost);

    // Bots — host only. Anyone else just sees them in the player list.
    const botCount = lobby.players.filter((p) => p.isBot).length;
    const humans = lobby.players.length - botCount;
    const botLabel = document.createElement('label');
    botLabel.className = 'wkr-label';
    botLabel.textContent = 'Bots';
    // Bots take seats on the grid, so the ceiling is whatever the humans left.
    const maxBots = Math.max(0, Math.min(MAX_BOTS, MAX_PLAYERS - humans));
    const botRow = this.buildStepper(
      botCount,
      0,
      maxBots,
      (n) => (n === 0 ? 'No bots' : n === 1 ? '1 bot' : `${n} bots`),
      (n) => this.callbacks.onSetBots(n),
      iAmHost,
    );

    // How hard those bots try. Shown to everyone so the grid knows what it is
    // up against, but only the host can move it.
    const skillLabel = document.createElement('label');
    skillLabel.className = 'wkr-label';
    skillLabel.textContent = 'Bot skill';
    const skillRow = this.buildSegmented(
      [...BOT_SKILLS],
      (skill) => BOT_SKILL_LABELS[skill],
      (skill) => skill === lobby.botSkill,
      (skill) => this.callbacks.onSetBotSkill(skill),
      iAmHost,
    );

    // Vehicle picker (re-pick calls onSelectVehicle).
    const vehicleLabel = document.createElement('label');
    vehicleLabel.className = 'wkr-label';
    vehicleLabel.textContent = 'Your ride';

    const picker = this.buildPicker((vehicleId) => {
      this.callbacks.onSelectVehicle(vehicleId);
    });

    // Ready toggle.
    const amReady = me?.ready ?? false;
    const readyBtn = document.createElement('button');
    readyBtn.className = amReady ? 'wkr-btn wkr-btn-ghost' : 'wkr-btn';
    readyBtn.type = 'button';
    readyBtn.textContent = amReady ? '✓ Ready — Cancel' : 'Ready Up';
    readyBtn.addEventListener('click', () => {
      this.callbacks.onToggleReady(!amReady);
    });

    const actions = document.createElement('div');
    actions.className = 'wkr-row';
    actions.appendChild(readyBtn);

    if (iAmHost) {
      const startBtn = document.createElement('button');
      startBtn.className = 'wkr-btn';
      startBtn.type = 'button';
      startBtn.textContent = 'Start Race';
      startBtn.addEventListener('click', () => this.callbacks.onStartRace());
      actions.appendChild(startBtn);
    }

    const leaveBtn = document.createElement('button');
    leaveBtn.className = 'wkr-btn wkr-btn-ghost';
    leaveBtn.type = 'button';
    leaveBtn.textContent = 'Leave';
    leaveBtn.addEventListener('click', () => this.callbacks.onLeaveLobby());
    actions.appendChild(leaveBtn);

    const status = this.buildStatusArea();

    const columns = document.createElement('div');
    columns.className = 'wkr-cols';
    const settings = document.createElement('div');
    settings.append(mapLabel, mapSelector, botLabel, botRow, skillLabel, skillRow);
    const ride = document.createElement('div');
    ride.append(vehicleLabel, picker);
    columns.append(settings, ride);

    panel.append(title, codeBox, hint, share, list, columns, actions, status);
  }

  /**
   * The lobby's shareable URL, alongside the code. A code has to be read out and
   * typed in; a link is one tap for whoever receives it, which is the difference
   * between "join my lobby" working on a phone and not.
   */
  private buildShareRow(code: string): HTMLElement {
    const row = document.createElement('div');
    row.className = 'wkr-share';

    const url = buildJoinUrl(window.location.href, code);

    const text = document.createElement('div');
    text.className = 'wkr-share-url';
    text.textContent = url;
    text.title = url; // the row truncates on narrow screens

    const copy = document.createElement('button');
    copy.className = 'wkr-btn wkr-btn-ghost wkr-btn-sm';
    copy.type = 'button';
    copy.textContent = '🔗 Copy link';
    copy.addEventListener('click', () => this.copyText(url, copy));

    row.append(text, copy);
    return row;
  }

  private buildPlayerRow(
    p: LobbyInfo['players'][number],
    myId: string,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'wkr-player';
    const isMe = p.id === myId;
    if (isMe) row.classList.add('wkr-me');
    if (!p.connected) row.classList.add('wkr-p-off');

    const emoji = document.createElement('div');
    emoji.className = 'wkr-p-emoji';
    emoji.textContent = this.vehicleById(p.vehicleId)?.emoji ?? '🚗';

    const name = document.createElement('div');
    name.className = 'wkr-p-name';
    name.textContent = p.name;
    // Signed in: this name is an account's, so nobody else can be racing under
    // it and the time will count. Without the mark, a typed name and an owned
    // one look identical in the roster.
    if (p.authed) {
      const check = document.createElement('span');
      check.className = 'wkr-p-check';
      check.textContent = '✓';
      check.title = 'Signed in — this racer is on the leaderboard';
      name.appendChild(check);
    }
    if (p.isHost) {
      const crown = document.createElement('span');
      crown.className = 'wkr-p-tag';
      crown.textContent = ' 👑';
      name.appendChild(crown);
    }
    if (isMe) {
      const meTag = document.createElement('span');
      meTag.className = 'wkr-p-tag';
      meTag.textContent = ' (you)';
      name.appendChild(meTag);
    }

    const badge = document.createElement('span');
    if (p.isBot) {
      badge.className = 'wkr-badge wkr-badge-bot';
      badge.textContent = '🤖 Bot';
    } else if (p.ready) {
      badge.className = 'wkr-badge wkr-badge-ready';
      badge.textContent = '✓ Ready';
    } else {
      badge.className = 'wkr-badge wkr-badge-notready';
      badge.textContent = '○ Not ready';
    }

    row.append(emoji, name, badge);
    return row;
  }

  /** Copy a code or a link to the clipboard, flashing the button that asked. */
  private copyText(text: string, btn: HTMLButtonElement): void {
    const done = (): void => {
      const prev = btn.textContent ?? 'Copy';
      btn.textContent = 'Copied!';
      window.setTimeout(() => {
        btn.textContent = prev === 'Copied!' ? 'Copy' : prev;
      }, 1400);
    };

    const fallback = (): void => {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        done();
      } catch {
        /* best-effort copy; ignore failures */
      }
    };

    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      navigator.clipboard.writeText(text).then(done, fallback);
    } else {
      fallback();
    }
  }

  // ---- Results ----------------------------------------------------------

  private renderResults(lobby: LobbyInfo, myId: string): void {
    const panel = this.reset();

    const players = lobby.players.slice().sort((a, b) => {
      const pa = a.place ?? Number.POSITIVE_INFINITY;
      const pb = b.place ?? Number.POSITIVE_INFINITY;
      if (pa !== pb) return pa - pb;
      // Fall back to finish time then name for stable ordering of non-finishers.
      const ta = a.finishTime ?? Number.POSITIVE_INFINITY;
      const tb = b.finishTime ?? Number.POSITIVE_INFINITY;
      if (ta !== tb) return ta - tb;
      return a.name.localeCompare(b.name);
    });

    const me = lobby.players.find((p) => p.id === myId) ?? null;

    const headline = document.createElement('div');
    headline.className = 'wkr-headline';
    if (me && me.place === 1) {
      headline.textContent = '🏆 You won!';
    } else if (me && me.place !== null) {
      headline.textContent = `You finished ${ordinal(me.place)}`;
    } else if (me) {
      headline.textContent = 'You did not finish';
    } else {
      headline.textContent = 'Race Results';
    }

    const table = document.createElement('table');
    table.className = 'wkr-table';

    const thead = document.createElement('thead');
    const htr = document.createElement('tr');
    for (const h of ['#', '', 'Racer', 'Time']) {
      const th = document.createElement('th');
      th.textContent = h;
      htr.appendChild(th);
    }
    thead.appendChild(htr);

    const tbody = document.createElement('tbody');
    for (const p of players) {
      const tr = document.createElement('tr');
      if (p.id === myId) tr.className = 'wkr-me';

      const placeTd = document.createElement('td');
      placeTd.className = 'wkr-t-place';
      placeTd.textContent = p.place !== null ? String(p.place) : '—';

      const emojiTd = document.createElement('td');
      emojiTd.className = 'wkr-t-emoji';
      emojiTd.textContent = this.vehicleById(p.vehicleId)?.emoji ?? '🚗';

      const nameTd = document.createElement('td');
      nameTd.textContent = p.name + (p.id === myId ? ' (you)' : '');

      const timeTd = document.createElement('td');
      timeTd.className = 'wkr-t-time';
      timeTd.textContent = p.finished ? formatTime(p.finishTime) : 'DNF';

      tr.append(placeTd, emojiTd, nameTd, timeTd);
      tbody.appendChild(tr);
    }

    table.append(thead, tbody);

    const actions = document.createElement('div');
    actions.className = 'wkr-row';

    const rematchBtn = document.createElement('button');
    rematchBtn.className = 'wkr-btn';
    rematchBtn.type = 'button';
    rematchBtn.textContent = 'Rematch';
    rematchBtn.addEventListener('click', () => this.callbacks.onRematch());

    const leaderboardBtn = document.createElement('button');
    leaderboardBtn.className = 'wkr-btn wkr-btn-ghost';
    leaderboardBtn.type = 'button';
    leaderboardBtn.textContent = '🏆 Leaderboard';
    leaderboardBtn.addEventListener('click', () => {
      this.callbacks.onShowLeaderboard(lobby.trackId);
    });

    const leaveBtn = document.createElement('button');
    leaveBtn.className = 'wkr-btn wkr-btn-ghost';
    leaveBtn.type = 'button';
    leaveBtn.textContent = 'Leave';
    leaveBtn.addEventListener('click', () => this.callbacks.onLeaveLobby());

    actions.append(rematchBtn, leaderboardBtn, leaveBtn);

    const status = this.buildStatusArea();

    panel.append(headline, table);

    // Tell a guest what just happened to their time, where they will care about
    // it — but only once they have actually set one.
    if (!this.account && me?.finished) {
      const note = document.createElement('div');
      note.className = 'wkr-hint wkr-account-note';
      note.style.marginBottom = '0';
      note.textContent =
        'Guest run — not saved. Sign in from the main menu to get on the leaderboard.';
      panel.append(note);
    }

    panel.append(actions, status);
  }

  // ---- Leaderboard ------------------------------------------------------

  private renderLeaderboard(
    trackId: string | null,
    entries: LeaderboardEntry[],
    tracks: TrackInfo[],
  ): void {
    const panel = this.reset(true);

    const title = document.createElement('div');
    title.className = 'wkr-title';
    const titleEmoji = document.createElement('span');
    titleEmoji.className = 'wkr-title-emoji';
    titleEmoji.textContent = '🏆';
    title.append(titleEmoji, document.createTextNode('Global Leaderboard'));

    // Filter buttons: "All" plus one per track. Clicking refetches.
    const filters = document.createElement('div');
    filters.className = 'wkr-filters';

    const makeFilter = (
      label: string,
      value: string | null,
      active: boolean,
    ): HTMLButtonElement => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = active
        ? 'wkr-btn wkr-btn-ghost wkr-filter wkr-filter-active'
        : 'wkr-btn wkr-btn-ghost wkr-filter';
      btn.textContent = label;
      btn.addEventListener('click', () => {
        this.callbacks.onShowLeaderboard(value);
      });
      return btn;
    };

    filters.appendChild(makeFilter('All', null, trackId === null));
    for (const t of tracks) {
      filters.appendChild(
        makeFilter(`${t.emoji} ${t.name}`, t.id, trackId === t.id),
      );
    }

    panel.append(title, filters);

    if (entries.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'wkr-lb-empty';
      empty.textContent = 'No times yet — be the first!';
      panel.append(empty);
    } else {
      const trackEmoji = (id: string): string =>
        tracks.find((t) => t.id === id)?.emoji ?? '🏁';
      const trackName = (id: string): string =>
        tracks.find((t) => t.id === id)?.name ?? id;

      const table = document.createElement('table');
      table.className = 'wkr-table';

      const thead = document.createElement('thead');
      const htr = document.createElement('tr');
      for (const h of ['#', 'Racer', 'Track', 'Time']) {
        const th = document.createElement('th');
        th.textContent = h;
        htr.appendChild(th);
      }
      thead.appendChild(htr);

      const tbody = document.createElement('tbody');
      entries.forEach((entry, i) => {
        const rank = i + 1;
        const tr = document.createElement('tr');
        // Rows are account names, so the signed-in player can be picked out.
        if (this.account && entry.name === this.account) tr.className = 'wkr-lb-mine';

        const rankTd = document.createElement('td');
        rankTd.className = 'wkr-t-place';
        const m = medal(rank);
        rankTd.textContent = m ? `${m} ${rank}` : String(rank);

        const nameTd = document.createElement('td');
        nameTd.textContent = entry.name;

        const trackTd = document.createElement('td');
        trackTd.className = 'wkr-lb-track';
        const tEmoji = document.createElement('span');
        tEmoji.className = 'wkr-lb-track-emoji';
        tEmoji.textContent = trackEmoji(entry.trackId);
        trackTd.append(tEmoji, document.createTextNode(trackName(entry.trackId)));

        const timeTd = document.createElement('td');
        timeTd.className = 'wkr-t-time';
        timeTd.textContent = formatTime(entry.timeMs);

        tr.append(rankTd, nameTd, trackTd, timeTd);
        tbody.appendChild(tr);
      });

      table.append(thead, tbody);
      panel.append(table);
    }

    const actions = document.createElement('div');
    actions.className = 'wkr-row';

    // Standing in front of the board is the moment wanting to be on it makes
    // sense, so the way on is right here.
    if (!this.account) {
      const note = document.createElement('div');
      note.className = 'wkr-hint wkr-account-note';
      note.style.marginTop = '0.75rem';
      note.style.marginBottom = '0';
      note.textContent = 'Only signed-in racers appear here.';
      panel.append(note);

      const signInBtn = document.createElement('button');
      signInBtn.className = 'wkr-btn';
      signInBtn.type = 'button';
      signInBtn.textContent = '👤 Sign in';
      signInBtn.addEventListener('click', () => this.callbacks.onShowAuth());
      actions.appendChild(signInBtn);
    }

    const backBtn = document.createElement('button');
    backBtn.className = 'wkr-btn wkr-btn-ghost wkr-btn-block';
    backBtn.type = 'button';
    backBtn.textContent = 'Back';
    backBtn.addEventListener('click', () => this.callbacks.onCloseLeaderboard());
    actions.appendChild(backBtn);

    const status = this.buildStatusArea();

    panel.append(actions, status);
  }
}
